import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import { API, graphqlOperation } from "aws-amplify";
import { trackCarByActiveByTrackBySortKeyMini } from "../../graphql/trackCar/trackCar-queries";

import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import { makeStyles } from "@material-ui/core/styles";
import { red, green, grey } from "@material-ui/core/colors";

import { useNotify } from "react-admin";

import {
  InboundState,
  InboundStatus,
  OutboundStatus,
  ResourceType,
} from "../../utils/constants";
import { logError } from "../../utils/appUtils";
import { getAisleFullName } from "../../utils/locationUtils";
import { compareSortKeys } from "../../utils/timestampSortKey";
import {
  getAccountColor,
  getProductColor,
  getTrackCarDisplayFields,
} from "../../utils/trackListUtils";
import {
  getSelectedRailcarContainerStyle,
  getSelectedRailcarIconColor,
} from "../../utils/selectedRailcarStyle";
import { useTrackCarSubscriptionRefresh } from "../../utils/useTrackCarSubscriptionRefresh";
import { InboundIcon, OutboundIcon } from "./iconography";
import { InlineRailcarVerificationControls } from "./InlineRailcarVerificationControls";
import { TrackLegend } from "../tracks/TrackLegend";

const useStyles = makeStyles((theme) => ({
  spinner: {
    display: "flex",
    width: "100%",
    justifyContent: "center", // Aligns children (e.g., CircularProgress) in the center horizontally
  },
  trackContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: "1px dashed",
    borderRadius: theme.spacing(1),
    borderColor: grey[400],
    padding: theme.spacing(1),
    minHeight: theme.spacing(8),
  },
  carContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(0.5),
    margin: theme.spacing(0.5),
    border: "1px solid",
    borderColor: grey[600],
    borderRadius: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
  },
  accountIndicator: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    zIndex: 1,
  },
  productIndicator: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 4,
    zIndex: 1,
  },
  shipmentCurrent: {
    ...getSelectedRailcarContainerStyle(theme),
  },
  inboundCompleted: {
    borderColor: green[600],
  },
  released: {
    borderColor: red[600],
  },
  carIndex: {
    display: "flex",
    flexDirection: "row",
    alignContent: "center",
    marginLeft: theme.spacing(0.5),
  },
  shipmentText: {
    display: "flex",
    flexDirection: "row",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
  },
  shipmentTextVerification: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    alignContent: "stretch",
    width: "100%",
    minWidth: 0,
  },
  verificationStrip: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "transparent",
  },
  carContainerVerificationLayout: {
    alignItems: "flex-start",
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderWidth: 2,
  },
  carContainerVerificationError: {
    boxShadow: `inset 0 0 0 2px ${theme.palette.error.main}`,
    backgroundColor: alpha(theme.palette.error.main, 0.14),
  },
}));

const TRACK_VIEW_TRUNCATE_THRESHOLD = 20;
const TRACK_VIEW_CONTEXT_SIZE = 5;
const TRACK_VIEW_WINDOW_SIZE = TRACK_VIEW_CONTEXT_SIZE * 2 + 1;

const RailcarVerificationBlock = ({
  classes,
  label,
  inputValue,
  inputError,
  helperText,
  onInputChange,
  onValidate,
}) => (
  <Box className={classes.verificationStrip}>
    <InlineRailcarVerificationControls
      label={label}
      inputValue={inputValue}
      inputError={inputError}
      helperText={helperText}
      onInputChange={onInputChange}
      onValidate={onValidate}
    />
  </Box>
);

const getTrackCarShipmentId = (trackCar) => {
  if (!trackCar) return null;
  if (trackCar.parentType === ResourceType.OUTBOUND) {
    return trackCar.outbound?.id || null;
  }
  return trackCar.inbound?.id || null;
};

export const TrackLocationView = ({
  inventoryItem,
  selectedTrack,
  highlightShipmentId,
  hideSelectedRailcar = false,
  sourceRailcarInput,
  sourceRailcarError = false,
  onSourceRailcarInputChange,
  onValidateSourceRailcar,
  targetRailcarInput,
  targetRailcarError = false,
  onTargetRailcarInputChange,
  onValidateTargetRailcar,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [processedCars, setProcessedCars] = useState([]);
  const lteSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const resolvedTrack = selectedTrack || inventoryItem.location;
  const anchorShipmentId =
    highlightShipmentId ||
    inventoryItem?.inboundShipmentId ||
    inventoryItem?.outboundShipmentId ||
    null;

  // Refs read by `shouldRefresh` below. They are written synchronously in
  // `getShipments` (after the fetch completes) and in the track-change effect
  // (below), so the subscription callback always sees the latest data even in
  // the microtask gap before React commits the next render.
  //
  // `initialFetchCompleteRef` widens `shouldRefresh` to any same-yard event
  // between mount / track-change and the first `getShipments` completion,
  // because the precise id/shipmentId fallback cannot match anything until
  // `processedCars` is populated for the current track.
  const processedCarsRef = useRef(processedCars);
  const resolvedTrackIdRef = useRef(resolvedTrack?.id);
  const initialFetchCompleteRef = useRef(false);

  useEffect(() => {
    resolvedTrackIdRef.current = resolvedTrack?.id;
    processedCarsRef.current = [];
    initialFetchCompleteRef.current = false;
  }, [resolvedTrack?.id]);

  const { visibleTrackCars, visibleTrackStartIndex } = useMemo(() => {
    const totalCars = processedCars.length;
    if (totalCars <= TRACK_VIEW_TRUNCATE_THRESHOLD) {
      return {
        visibleTrackCars: processedCars,
        visibleTrackStartIndex: 0,
      };
    }

    let anchorIndex = -1;
    if (anchorShipmentId) {
      anchorIndex = processedCars.findIndex(
        (trackCar) => getTrackCarShipmentId(trackCar) === anchorShipmentId,
      );
    }
    if (anchorIndex < 0) {
      anchorIndex = 0;
    }

    let startIndex = Math.max(0, anchorIndex - TRACK_VIEW_CONTEXT_SIZE);
    let endIndex = Math.min(totalCars, startIndex + TRACK_VIEW_WINDOW_SIZE);
    startIndex = Math.max(0, endIndex - TRACK_VIEW_WINDOW_SIZE);

    return {
      visibleTrackCars: processedCars.slice(startIndex, endIndex),
      visibleTrackStartIndex: startIndex,
    };
  }, [anchorShipmentId, processedCars]);

  const getShipments = useCallback(async () => {
    try {
      if (!resolvedTrack?.id) return;
      setLoading(true);
      const items = [];
      let nextToken = null;

      do {
        const response = await API.graphql(
          graphqlOperation(trackCarByActiveByTrackBySortKeyMini, {
            reloadYardId: window.appUser.reloadYardId,
            activeTrackIdSortKey: {
              beginsWith: { active: "true", trackId: resolvedTrack.id },
            },
            sortDirection: "ASC",
            limit: 500,
            nextToken,
          }),
        );
        const trackCarResult = response?.data?.trackCarByActiveByTrackBySortKey;
        if (trackCarResult?.items?.length) {
          items.push(...trackCarResult.items);
        }
        nextToken = trackCarResult?.nextToken || null;
      } while (nextToken);

      const processedCarsList = items.sort((a, b) =>
        compareSortKeys(a.sortKey || "", b.sortKey || ""),
      );

      setProcessedCars(processedCarsList);
      processedCarsRef.current = processedCarsList;
      initialFetchCompleteRef.current = true;
    } catch (err) {
      logError("TrackLocationView:getShipments", err, notify);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedTrack]);

  useEffect(() => {
    if (resolvedTrack) getShipments();
  }, [resolvedTrack, getShipments]);

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(resolvedTrack?.id),
    label: "TrackLocationView",
    shouldRefresh: (trackCar) => {
      if (!trackCar) return false;

      const currentTrackId = resolvedTrackIdRef.current;
      if (currentTrackId && trackCar.trackId === currentTrackId) {
        return true;
      }

      // Initial fetch (or post-track-change re-fetch) hasn't populated
      // processedCars yet; the id/shipmentId fallback below would always
      // miss. Widen to any same-yard event so a TC update arriving in that
      // gap still triggers a refresh.
      if (!initialFetchCompleteRef.current) {
        return trackCar?.reloadYardId === window.appUser?.reloadYardId;
      }

      return processedCarsRef.current.some(
        (currentTrackCar) =>
          currentTrackCar.id === trackCar.id ||
          currentTrackCar.shipmentId === trackCar.shipmentId,
      );
    },
    onRefresh: () => {
      getShipments();
    },
  });

  if (!resolvedTrack) return <></>;
  return (
    <>
      <TrackLegend selectedTrack={resolvedTrack} />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        mb={1}
      >
        <Typography variant="overline" display="block">
          {`${getAisleFullName(resolvedTrack)} Track Head`}
        </Typography>
      </Box>
      <div className={classes.trackContainer}>
        {loading && (
          <div className={classes.spinner}>
            <CircularProgress color="inherit" />
          </div>
        )}
        {visibleTrackCars.map((trackCar, idx) =>
          trackCar.parentType === ResourceType.OUTBOUND ? (
            <OutboundCar
              key={trackCar.id || trackCar.railCarNumber || idx}
              classes={classes}
              lteSmall={lteSmall}
              inventoryItem={inventoryItem}
              highlightShipmentId={highlightShipmentId}
              hideSelectedRailcar={hideSelectedRailcar}
              sourceRailcarInput={sourceRailcarInput}
              sourceRailcarError={sourceRailcarError}
              onSourceRailcarInputChange={onSourceRailcarInputChange}
              onValidateSourceRailcar={onValidateSourceRailcar}
              targetRailcarInput={targetRailcarInput}
              targetRailcarError={targetRailcarError}
              onTargetRailcarInputChange={onTargetRailcarInputChange}
              onValidateTargetRailcar={onValidateTargetRailcar}
              trackCar={trackCar}
              idx={visibleTrackStartIndex + idx}
            />
          ) : (
            <InboundCar
              key={trackCar.id || trackCar.railCarNumber || idx}
              classes={classes}
              lteSmall={lteSmall}
              inventoryItem={inventoryItem}
              highlightShipmentId={highlightShipmentId}
              hideSelectedRailcar={hideSelectedRailcar}
              sourceRailcarInput={sourceRailcarInput}
              sourceRailcarError={sourceRailcarError}
              onSourceRailcarInputChange={onSourceRailcarInputChange}
              onValidateSourceRailcar={onValidateSourceRailcar}
              trackCar={trackCar}
              idx={visibleTrackStartIndex + idx}
            />
          ),
        )}
      </div>
    </>
  );
};

const InboundCar = ({
  classes,
  lteSmall,
  inventoryItem,
  trackCar,
  idx,
  highlightShipmentId,
  hideSelectedRailcar,
  sourceRailcarInput,
  sourceRailcarError,
  onSourceRailcarInputChange,
  onValidateSourceRailcar,
}) => {
  const {
    shipment,
    accountName,
    accountId,
    accountColor: shipmentAccountColor,
    railCarNumber,
    productColor,
    productId,
  } = getTrackCarDisplayFields(trackCar);
  const currentShipmentId =
    highlightShipmentId || inventoryItem.inboundShipmentId;
  const isCurrentCar = shipment?.id === currentShipmentId;
  const displayRailCarNumber =
    hideSelectedRailcar && isCurrentCar ? "Hidden" : railCarNumber;
  const showSourceVerification =
    hideSelectedRailcar &&
    isCurrentCar &&
    onSourceRailcarInputChange &&
    onValidateSourceRailcar;
  const getCarClassName = () => {
    const classNames = [classes.carContainer];
    if (showSourceVerification)
      classNames.push(classes.carContainerVerificationLayout);
    if (isCurrentCar) classNames.push(classes.shipmentCurrent);
    if (!isCurrentCar && shipment?.status === InboundStatus.RELEASED)
      classNames.push(classes.released);
    else if (!isCurrentCar && shipment?.state === InboundState.COMPLETED)
      classNames.push(classes.inboundCompleted);
    if (showSourceVerification && sourceRailcarError)
      classNames.push(classes.carContainerVerificationError);
    return classNames.join(" ");
  };

  const getIconStyles = () => {
    const styles = { marginLeft: 4 };
    if (isCurrentCar) {
      styles.color = getSelectedRailcarIconColor();
      return styles;
    }
    if (shipment?.status === InboundStatus.RELEASED) styles.color = red[700];
    else if (shipment?.state === InboundState.COMPLETED)
      styles.color = green[700];
    return styles;
  };

  const accountColor = shipmentAccountColor || getAccountColor(accountId);
  // Product indicator is productId-based; no commodity fallback.
  const resolvedProductColor = productColor || getProductColor(productId);

  return (
    <div className={getCarClassName()}>
      {accountColor && (
        <div
          className={classes.accountIndicator}
          style={{ backgroundColor: accountColor }}
        />
      )}
      {resolvedProductColor && (
        <div
          className={classes.productIndicator}
          style={{ backgroundColor: resolvedProductColor }}
        />
      )}
      <div className={classes.carIndex}>
        <Typography>&nbsp;{idx + 1 + "."} </Typography>
        <InboundIcon style={getIconStyles()} />
      </div>
      <div
        className={
          showSourceVerification
            ? classes.shipmentTextVerification
            : classes.shipmentText
        }
      >
        {showSourceVerification ? (
          <RailcarVerificationBlock
            classes={classes}
            label="Supplying railcar number"
            inputValue={sourceRailcarInput}
            inputError={sourceRailcarError}
            helperText={
              sourceRailcarError
                ? "Railcar number does not match the expected supplying railcar."
                : ""
            }
            onInputChange={onSourceRailcarInputChange}
            onValidate={onValidateSourceRailcar}
          />
        ) : lteSmall ? (
          <div>
            <Typography>{displayRailCarNumber}</Typography>
            <Typography>{accountName}</Typography>
            <Typography>{shipment?.poNumber}</Typography>
          </div>
        ) : (
          <Typography>
            {`${displayRailCarNumber || ""} - ${accountName || ""} (${
              shipment?.poNumber || ""
            })`}
          </Typography>
        )}
      </div>
    </div>
  );
};

const OutboundCar = ({
  classes,
  lteSmall,
  inventoryItem,
  trackCar,
  idx,
  highlightShipmentId,
  hideSelectedRailcar,
  sourceRailcarInput,
  sourceRailcarError,
  onSourceRailcarInputChange,
  onValidateSourceRailcar,
  targetRailcarInput,
  targetRailcarError,
  onTargetRailcarInputChange,
  onValidateTargetRailcar,
}) => {
  const {
    shipment,
    accountName,
    accountId,
    accountColor: shipmentAccountColor,
    railCarNumber,
    productColor,
    productId,
  } = getTrackCarDisplayFields(trackCar);
  const currentShipmentId =
    highlightShipmentId || inventoryItem.outboundShipmentId;
  const isCurrentCar = shipment?.id === currentShipmentId;
  const hasSourceVerificationControls =
    onSourceRailcarInputChange && onValidateSourceRailcar;
  const hasTargetVerificationControls =
    onTargetRailcarInputChange && onValidateTargetRailcar;
  const displayRailCarNumber =
    hideSelectedRailcar &&
    isCurrentCar &&
    (hasSourceVerificationControls || hasTargetVerificationControls)
      ? "Hidden"
      : railCarNumber;
  const showSourceVerification =
    hideSelectedRailcar && isCurrentCar && hasSourceVerificationControls;
  const showTargetVerification =
    hideSelectedRailcar && isCurrentCar && hasTargetVerificationControls;
  const showAnyVerification = showSourceVerification || showTargetVerification;
  const verificationInputError =
    (showSourceVerification && sourceRailcarError) ||
    (showTargetVerification && targetRailcarError);
  const getCarClassName = () => {
    const classNames = [classes.carContainer];
    if (showAnyVerification)
      classNames.push(classes.carContainerVerificationLayout);
    if (isCurrentCar) classNames.push(classes.shipmentCurrent);
    if (!isCurrentCar && shipment?.status === OutboundStatus.RELEASED)
      classNames.push(classes.released);
    if (showAnyVerification && verificationInputError)
      classNames.push(classes.carContainerVerificationError);
    return classNames.join(" ");
  };

  const getIconStyles = () => {
    const styles = { marginLeft: 4 };
    if (shipment?.id === currentShipmentId) {
      styles.color = getSelectedRailcarIconColor();
      return styles;
    }
    if (shipment?.status === OutboundStatus.RELEASED) styles.color = red[700];
    return styles;
  };

  const accountColor = shipmentAccountColor || getAccountColor(accountId);
  // Product indicator is productId-based; no commodity fallback.
  const resolvedProductColor = productColor || getProductColor(productId);

  return (
    <div className={getCarClassName()}>
      {accountColor && (
        <div
          className={classes.accountIndicator}
          style={{ backgroundColor: accountColor }}
        />
      )}
      {resolvedProductColor && (
        <div
          className={classes.productIndicator}
          style={{ backgroundColor: resolvedProductColor }}
        />
      )}
      <div className={classes.carIndex}>
        <Typography> &nbsp;{idx + 1 + ". "} </Typography>
        <OutboundIcon style={getIconStyles()} />
      </div>
      <div
        className={
          showAnyVerification
            ? classes.shipmentTextVerification
            : classes.shipmentText
        }
      >
        {showSourceVerification ? (
          <RailcarVerificationBlock
            classes={classes}
            label="Supplying railcar number"
            inputValue={sourceRailcarInput}
            inputError={sourceRailcarError}
            helperText={
              sourceRailcarError
                ? "Railcar number does not match the expected supplying railcar."
                : ""
            }
            onInputChange={onSourceRailcarInputChange}
            onValidate={onValidateSourceRailcar}
          />
        ) : showTargetVerification ? (
          <RailcarVerificationBlock
            classes={classes}
            label="Receiving railcar number"
            inputValue={targetRailcarInput}
            inputError={targetRailcarError}
            helperText={
              targetRailcarError
                ? "Railcar number does not match the selected receiving railcar."
                : ""
            }
            onInputChange={onTargetRailcarInputChange}
            onValidate={onValidateTargetRailcar}
          />
        ) : lteSmall ? (
          <div>
            <Typography>{displayRailCarNumber}</Typography>
            <Typography>{accountName}</Typography>
            <Typography>{shipment?.poNumber}</Typography>
          </div>
        ) : (
          <Typography>
            {`${displayRailCarNumber || ""} - ${accountName || ""} (${
              shipment?.poNumber || ""
            })`}
          </Typography>
        )}
      </div>
    </div>
  );
};
